import type { Signal} from '@angular/core';
import { Injectable, computed, inject } from '@angular/core';

import { AuthStore } from '../auth/auth.store';
import type { Permission } from './permissions';

/**
 * RBAC en UI (defensa en profundidad; la autorización REAL es del backend).
 *
 * Los permisos se DERIVAN de los claims firmados del JWT con un `computed`
 * (estado derivado, no sincronizado: un effect que escribe signals lanza
 * NG0600 en Angular 18 y dejaría el set vacío para siempre).
 * Expone `can(p): Signal<boolean>` para bindear en templates/guards sin
 * recalcular en cada CD. `UI oculta por UX, backend autoriza por seguridad.`
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly auth = inject(AuthStore);

  private readonly _permissions = computed<ReadonlySet<string>>(
    () => new Set(this.auth.permissions()),
  );
  readonly permissions: Signal<ReadonlySet<string>> = this._permissions;

  /** Cache de señales `can()` por permiso (evita crear computed duplicados). */
  private readonly canCache = new Map<string, Signal<boolean>>();

  /** Señal reactiva: ¿el usuario tiene el permiso `p`? */
  can(p: Permission | string): Signal<boolean> {
    let sig = this.canCache.get(p);
    if (!sig) {
      sig = computed(() => this._permissions().has(p));
      this.canCache.set(p, sig);
    }
    return sig;
  }

  /** Chequeo imperativo puntual (guards, lógica no reactiva). */
  hasPermission(p: Permission | string): boolean {
    return this._permissions().has(p);
  }

  /** True si tiene TODOS los permisos. */
  hasAll(perms: readonly (Permission | string)[]): boolean {
    const set = this._permissions();
    return perms.every((p) => set.has(p));
  }

  /** True si tiene AL MENOS UNO de los permisos. */
  hasAny(perms: readonly (Permission | string)[]): boolean {
    const set = this._permissions();
    return perms.some((p) => set.has(p));
  }
}
